import {
    Button,
    Group,
    Modal,
    Stack,
    Text,
    TextInput,
    Textarea,
    Title,
    Alert,
    Loader
} from '@mantine/core';
import { Formik, Field } from 'formik';
import * as Yup from 'yup';
import { useState } from 'react';
import {
    AiOutlineClose,
    AiOutlinePlus,
    AiOutlineWarning,
    AiOutlineCheckCircle
} from 'react-icons/ai';
import { Registrar } from '../../registrars/types';
import { RegistrarService, CreateRegistrarInput } from '../services/RegistrarService';
import { useAuth } from '../../../hooks/useAuth';

export interface AddRegistrarModalProps {
    isOpen: boolean;
    onClose: () => void;
    onRegistrarCreated: (registrar: Registrar) => void;
}

// Validation schema for the registrar form
const registrarValidationSchema = Yup.object().shape({
    name: Yup.string()
        .required('Registrar name is required')
        .min(2, 'Name must be at least 2 characters')
        .max(100, 'Name must be less than 100 characters')
        .matches(/^[a-zA-Z0-9\s\-_.]+$/, 'Name can only contain letters, numbers, spaces, hyphens, underscores, and periods'),
    description: Yup.string()
        .max(500, 'Description must be less than 500 characters'),
    website: Yup.string()
        .url('Please enter a valid website URL')
        .nullable()
});

export const AddRegistrarModal = ({
    isOpen,
    onClose,
    onRegistrarCreated
}: AddRegistrarModalProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    useAuth();

    const initialValues: CreateRegistrarInput = {
        name: '',
        description: '',
        website: ''
    };

    const handleSubmit = async (values: CreateRegistrarInput, { resetForm }: { resetForm: () => void }) => {
        setIsLoading(true);
        setError(null);

        try {
            // Check if registrar already exists
            const exists = await RegistrarService.checkRegistrarExists(values.name);
            if (exists) {
                setError(`A registrar with the name "${values.name}" already exists. Please choose a different name.`);
                setIsLoading(false);
                return;
            }

            // Clean up the input data
            const cleanedInput: CreateRegistrarInput = {
                name: values.name.trim(),
                description: values.description?.trim() || undefined,
                website: values.website?.trim() || undefined
            };

            // If website is provided, ensure it has a protocol
            if (cleanedInput.website && !cleanedInput.website.startsWith('http')) {
                cleanedInput.website = `https://${cleanedInput.website}`;
            }

            const newRegistrar = await RegistrarService.createRegistrar(cleanedInput);
            
            if (newRegistrar) {
                onRegistrarCreated(newRegistrar);
                handleClose(resetForm);
            }
        } catch (error) {
            console.error('Failed to create registrar:', error);
            setError('Failed to create registrar. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = (resetForm?: () => void) => {
        if (resetForm) {
            resetForm();
        }
        setError(null);
        setIsLoading(false);
        onClose();
    };

    return (
        <Modal
            opened={isOpen}
            onClose={() => handleClose()}
            title={
                <Group gap="sm">
                    <AiOutlinePlus />
                    <Title order={4}>Add New Registrar</Title>
                </Group>
            }
            size="md"
            closeOnClickOutside={!isLoading}
            closeOnEscape={!isLoading}
            withCloseButton={!isLoading}
        >
            <Formik
                initialValues={initialValues}
                validationSchema={registrarValidationSchema}
                onSubmit={handleSubmit}
            >
                {({ handleSubmit: formikHandleSubmit, resetForm }) => (
                    <form onSubmit={formikHandleSubmit}>
                        <Stack gap="md">
                            {/* Error Alert */}
                            {error && (
                                <Alert
                                    color="red"
                                    variant="light"
                                    icon={<AiOutlineWarning />}
                                    title="Error"
                                >
                                    {error}
                                </Alert>
                            )}

                            {/* Success Info */}
                            <Alert
                                color="blue"
                                variant="light"
                                icon={<AiOutlineCheckCircle />}
                                title="Create New Registrar"
                            >
                                <Text size="sm">
                                    Add a new registrar that will be available for all users in the system. 
                                    This registrar will be immediately available for selection in your import.
                                </Text>
                            </Alert>

                            {/* Registrar Name */}
                            <Field name="name">
                                {({ field, meta }: any) => (
                                    <TextInput
                                        label="Registrar Name"
                                        placeholder="Enter registrar name (e.g., GoDaddy, Namecheap)"
                                        required
                                        disabled={isLoading}
                                        error={meta.touched && meta.error ? meta.error : null}
                                        {...field}
                                    />
                                )}
                            </Field>

                            {/* Description */}
                            <Field name="description">
                                {({ field, meta }: any) => (
                                    <Textarea
                                        label="Description"
                                        placeholder="Optional description of the registrar"
                                        disabled={isLoading}
                                        rows={3}
                                        error={meta.touched && meta.error ? meta.error : null}
                                        {...field}
                                    />
                                )}
                            </Field>

                            {/* Website */}
                            <Field name="website">
                                {({ field, meta }: any) => (
                                    <TextInput
                                        label="Website"
                                        placeholder="Optional website URL (e.g., https://godaddy.com)"
                                        disabled={isLoading}
                                        error={meta.touched && meta.error ? meta.error : null}
                                        {...field}
                                    />
                                )}
                            </Field>

                            {/* Action Buttons */}
                            <Group justify="space-between" mt="md">
                                <Button
                                    variant="outline"
                                    leftSection={<AiOutlineClose />}
                                    onClick={() => handleClose(resetForm)}
                                    disabled={isLoading}
                                >
                                    Cancel
                                </Button>
                                
                                <Button
                                    type="submit"
                                    leftSection={isLoading ? <Loader size="xs" /> : <AiOutlinePlus />}
                                    disabled={isLoading}
                                    loading={isLoading}
                                >
                                    {isLoading ? 'Creating...' : 'Create Registrar'}
                                </Button>
                            </Group>
                        </Stack>
                    </form>
                )}
            </Formik>
        </Modal>
    );
};